import React from "react";

import AuditActionLink from "../../audit/AuditActionLink";
import PurchaseOrderStatusBadge from "./PurchaseOrderStatusBadge";
import type { PurchaseOrderStatus } from "../../../purchaseOrders";

const btnBase =
  "inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold";
const btnSecondary = `${btnBase} border border-[#E2E8F0] bg-white text-[#2563EB] hover:bg-[#EFF6FF]`;
const btnPrimary = `${btnBase} border border-[#2563EB] bg-[#2563EB] text-white hover:bg-[#1D4ED8]`;
const btnPrimaryDisabled = `${btnPrimary} disabled:opacity-50`;
const btnSecondaryDisabled = `${btnSecondary} disabled:opacity-50`;
const btnReceive = `${btnBase} border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700`;

export interface PurchaseOrderActionPermissions {
  canApprove: boolean;
  canReceive: boolean;
  canClose: boolean;
  canReopen: boolean;
}

export interface PurchaseOrderHeaderActionsProps
  extends PurchaseOrderActionPermissions {
  purchaseOrderId: number | null;
  poStatus: PurchaseOrderStatus | string;
  poNumber: string | null;
  busy: boolean;
  onApprove: () => void;
  onClose: () => void;
  onReopen: () => void;
}

export function PurchaseOrderHeaderActions({
  purchaseOrderId,
  poStatus,
  poNumber,
  busy,
  canApprove,
  canReceive,
  canClose,
  canReopen,
  onApprove,
  onClose,
  onReopen,
}: PurchaseOrderHeaderActionsProps) {
  if (!purchaseOrderId) {
    return <PurchaseOrderStatusBadge status="draft" />;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <PurchaseOrderStatusBadge status={poStatus} />
      <AuditActionLink
        slug="purchase-order"
        id={purchaseOrderId}
        name={poNumber}
      />
      {canApprove && (
        <button
          type="button"
          disabled={busy}
          className={btnPrimaryDisabled}
          onClick={onApprove}
        >
          Approve
        </button>
      )}
      {canReceive && (
        <button
          type="button"
          className={btnReceive}
          onClick={() =>
            window.open(
              `/purchase-orders/${purchaseOrderId}/receive`,
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          Receive delivery
        </button>
      )}
      {canClose && (
        <button
          type="button"
          disabled={busy}
          className={btnSecondaryDisabled}
          onClick={onClose}
        >
          Close PO
        </button>
      )}
      {canReopen && (
        <button
          type="button"
          disabled={busy}
          className={btnSecondaryDisabled}
          onClick={onReopen}
        >
          Reopen
        </button>
      )}
    </div>
  );
}

export interface PurchaseOrderFooterActionsProps
  extends PurchaseOrderActionPermissions {
  isNew: boolean;
  busy: boolean;
  loading: boolean;
  saving: boolean;
  actionLoading: boolean;
  saveLabel: string;
  onSave: () => void;
  onCreateAndApprove: () => void;
  onApprove: () => void;
}

export function PurchaseOrderFooterActions({
  isNew,
  busy,
  loading,
  saving,
  actionLoading,
  saveLabel,
  canApprove,
  onSave,
  onCreateAndApprove,
  onApprove,
}: PurchaseOrderFooterActionsProps) {
  const disabled = busy || loading;

  return (
    <>
      <button
        type="button"
        className={btnSecondary}
        disabled={disabled}
        onClick={onSave}
      >
        {saving ? "Saving…" : saveLabel}
      </button>
      {isNew ? (
        <button
          type="button"
          className={btnPrimary}
          disabled={disabled}
          onClick={onCreateAndApprove}
        >
          {saving ? "Saving…" : "Create & approve"}
        </button>
      ) : (
        canApprove && (
          <button
            type="button"
            className={btnPrimary}
            disabled={disabled}
            onClick={onApprove}
          >
            {actionLoading ? "Working…" : "Approve"}
          </button>
        )
      )}
    </>
  );
}
